import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ticketsApi, type TicketDraft, type TicketDraftInput } from '../api/tickets';
import { getCachedCsrfToken, tokenManager } from '../api/client';

/* Server-side compose-box drafts.
 *
 * Whatever is in the reply / internal-note box is mirrored to the server a
 * moment after typing stops and read back the next time the ticket opens —
 * on any device. The new-ticket form gets by with sessionStorage (per tab),
 * but a reply draft has to survive "I clicked Home", a reload, and moving to
 * another machine, and the ticket list wants to show who has one. Nothing
 * here sends anything to anyone: a draft becomes a message only via Send.
 *
 * Wiring: pass the live compose values in; the hook decides when to save.
 * On open it fetches the saved draft and hands it to `onRestore` so the page
 * can seed its state, then autosave arms. `markSent` / `discard` retire it.
 */

const SAVE_DELAY_MS = 800;
/** fetch(keepalive) bodies are capped at 64KB; bigger drafts go via axios. */
const KEEPALIVE_MAX_BYTES = 60_000;

export type DraftSaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface UseTicketDraftOptions {
  enabled: boolean;
  /** Hold autosave while a send is in flight. */
  paused?: boolean;
  /** Seed the compose box from a saved draft. Called at most once per ticket. */
  onRestore: (draft: TicketDraft) => void;
}

export interface UseTicketDraftResult {
  /** The draft found on the server when this ticket opened; null once dismissed/retired. */
  restored: TicketDraft | null;
  /** True once the server has been asked — autosave stays off until then. */
  loaded: boolean;
  saveState: DraftSaveState;
  savedAt: Date | null;
  /** Throw the draft away (server + local bookkeeping). */
  discard: () => Promise<void>;
  /** The message was sent — the server already removed the draft. */
  markSent: () => void;
  /** Hide the "restored" notice without discarding anything. */
  dismissRestored: () => void;
}

interface PendingSave {
  ticketId: string;
  body: TicketDraftInput | null;   // null = clear
  serialized: string;
}

const isEmpty = (v: TicketDraftInput) => !v.content.trim() && v.attachments.length === 0;

export function useTicketDraft(
  ticketId: string | undefined,
  values: TicketDraftInput,
  opts: UseTicketDraftOptions,
): UseTicketDraftResult {
  const [restored, setRestored] = useState<TicketDraft | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saveState, setSaveState] = useState<DraftSaveState>('idle');
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const onRestore = useRef(opts.onRestore);
  onRestore.current = opts.onRestore;

  // Bookkeeping that must not trigger renders.
  const hasServerDraft = useRef(false);
  const lastSaved = useRef<string | null>(null);
  const pending = useRef<PendingSave | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped whenever the draft is retired (sent, discarded, ticket changed):
  // a save that was already on the wire then finishes by undoing itself.
  const generation = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const { type, content, attachments, status } = values;
  const serialized = useMemo(
    () => JSON.stringify({ type, content, attachments, status }),
    [type, content, attachments, status],
  );
  const latest = useRef(values);
  latest.current = values;

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  // ── Load once per ticket ────────────────────────────────────────────────
  useEffect(() => {
    generation.current += 1;
    clearTimer();
    pending.current = null;
    hasServerDraft.current = false;
    lastSaved.current = null;
    setRestored(null);
    setLoaded(false);
    setSaveState('idle');
    setSavedAt(null);
    if (!ticketId || !opts.enabled) return;

    const gen = generation.current;
    ticketsApi.getDraft(ticketId)
      .then(draft => {
        if (generation.current !== gen || !mounted.current) return;
        if (draft) {
          hasServerDraft.current = true;
          // What the page will now hold equals what's saved — no need to
          // write it straight back.
          lastSaved.current = JSON.stringify({
            type: draft.type,
            content: draft.content,
            attachments: draft.attachments ?? [],
            status: draft.status ?? null,
          });
          setRestored(draft);
          setSavedAt(new Date(draft.updatedAt));
          setSaveState('saved');
          onRestore.current(draft);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (generation.current === gen && mounted.current) setLoaded(true);
      });
  }, [ticketId, opts.enabled]);

  // ── Save the pending job (debounced path) ───────────────────────────────
  const runPending = useCallback(async () => {
    const job = pending.current;
    if (!job) return;
    pending.current = null;
    const gen = generation.current;
    try {
      if (job.body) await ticketsApi.saveDraft(job.ticketId, job.body);
      else await ticketsApi.deleteDraft(job.ticketId);
      if (generation.current !== gen) {
        // Retired while this save was in flight (the message went out, or
        // the draft was discarded): don't let it resurrect the row.
        if (job.body) ticketsApi.deleteDraft(job.ticketId).catch(() => undefined);
        return;
      }
      hasServerDraft.current = !!job.body;
      lastSaved.current = job.serialized;
      if (mounted.current) {
        setSaveState(job.body ? 'saved' : 'idle');
        setSavedAt(job.body ? new Date() : null);
      }
    } catch {
      if (generation.current === gen && mounted.current) setSaveState('error');
    }
  }, []);

  // ── Flush without waiting (navigation / tab hide) ───────────────────────
  const flushNow = useCallback(() => {
    const job = pending.current;
    if (!job) return;
    clearTimer();
    pending.current = null;

    const csrf = getCachedCsrfToken();
    const bodyStr = job.body ? JSON.stringify(job.body) : '';
    // No token yet (nothing mutating has run this session) or too big for
    // keepalive: axios still completes across an in-app navigation, it just
    // can't survive a tab close.
    if (!csrf || bodyStr.length > KEEPALIVE_MAX_BYTES) {
      const p = job.body ? ticketsApi.saveDraft(job.ticketId, job.body) : ticketsApi.deleteDraft(job.ticketId);
      p.catch(() => undefined);
    } else {
      const headers: Record<string, string> = { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf };
      const companyId = tokenManager.getCompanyId();
      if (companyId) headers['X-Company-Id'] = companyId;
      try {
        void fetch(`/api/tickets/${job.ticketId}/draft`, {
          method: job.body ? 'PUT' : 'DELETE',
          keepalive: true,
          credentials: 'include',
          headers,
          body: job.body ? bodyStr : undefined,
        });
      } catch {
        /* best effort — the next keystroke re-saves anyway */
      }
    }
    // Optimistic: the request is on its way and there is no one to tell if
    // it fails; a later edit resaves regardless.
    hasServerDraft.current = !!job.body;
    lastSaved.current = job.serialized;
    if (mounted.current) {
      setSaveState(job.body ? 'saved' : 'idle');
      if (job.body) setSavedAt(new Date());
    }
  }, []);

  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flushNow(); };
    window.addEventListener('pagehide', flushNow);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', flushNow);
      document.removeEventListener('visibilitychange', onHide);
      // Unmount = the user navigated away mid-debounce. This is the exact
      // "clicked Home and lost it" case, so the pending save goes out now.
      flushNow();
    };
  }, [flushNow]);

  // ── Autosave ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ticketId || !opts.enabled || !loaded || opts.paused) return;
    if (serialized === lastSaved.current) return;
    const body = isEmpty(latest.current) ? null : latest.current;
    if (body === null && !hasServerDraft.current) {
      // Empty box, nothing saved: nothing to do (and nothing to "save").
      lastSaved.current = serialized;
      return;
    }
    pending.current = { ticketId, body, serialized };
    setSaveState('saving');
    clearTimer();
    timer.current = setTimeout(() => {
      timer.current = null;
      void runPending();
    }, SAVE_DELAY_MS);
    return clearTimer;
  }, [serialized, ticketId, loaded, opts.enabled, opts.paused, runPending]);

  // ── Retire ──────────────────────────────────────────────────────────────
  const markSent = useCallback(() => {
    generation.current += 1;
    clearTimer();
    pending.current = null;
    hasServerDraft.current = false;
    lastSaved.current = null;
    setRestored(null);
    setSaveState('idle');
    setSavedAt(null);
  }, []);

  const discard = useCallback(async () => {
    const had = hasServerDraft.current || !!pending.current;
    markSent();
    if (had && ticketId) await ticketsApi.deleteDraft(ticketId).catch(() => undefined);
  }, [ticketId, markSent]);

  const dismissRestored = useCallback(() => setRestored(null), []);

  return { restored, loaded, saveState, savedAt, discard, markSent, dismissRestored };
}
